import {ChartAxisY, ChartDataSeriesOptions} from 'canvasjs';
import {QueryList} from '@angular/core';
import {FunctionName, FunctionParams} from './chart-functions/chart-function-types';
import {MiniChartComponent} from '@app/layout/charts/mini-chart/mini-chart.component';
import moment from 'moment-timezone';
import {ChartComponent} from "@shared/model/chart-component.interface";
import {ChartFunctionDataBuffer} from './chart-functions/chart-function-data-buffer';
import {Favourite} from '@app/graphql-typings/graphql';


export interface FavouriteFunctionSeriesData {
  chartFunctionDataBuffer: ChartFunctionDataBuffer;
  series: ChartDataSeriesOptions;
}

export interface FavouriteSeriesData {
  series: ChartDataSeriesOptions;
  functionSeries: FavouriteFunctionSeriesData[];
}

export interface FavouriteTransformData {
  transformFn: (v: number) => number;
  unitFn: () => string;
}

export enum SplitMode {
  BY_UNIT = 1,
  BY_EQUIPMENT,
  BY_ALL
}

export class AlertValues {
  readonly ALERT: number = 1;
  readonly NO_ALERT: number = null;

  getValue(value: boolean) {
    return value ? this.ALERT : this.NO_ALERT;
  }
}

interface AxisRange {
  viewportMinimum?: number;
  viewportMaximum?: number;
}

export type IsoWeekday = number;

export class ChartsUtil {
  static readonly CHART_MENU_DYNAMIC_HEIGHT_DATA_ATTRIBUTE = 'data-back-to-dynamic-height-custom-menu';
  static readonly CHART_MENU_DEFAULT_DATA_ATTRIBUTE = 'data-default-custom-menu';
  static readonly CHART_END_TS_ADJUSTMENT = moment.duration(1, 'day');
  static readonly ALERT_VALUES = new AlertValues();

  private static readonly CHART_PADDING_PX = 11;
  private static readonly CHART_TITLE_HEIGHT = 12;

  private static readonly PREVIOUS_ID = '-Previous';
  private static readonly COUNT_AGGREGATION_LABEL = 'COUNT';

  static getLegendForSpecificFavourite(favouriteData: FavouriteData, functionParams?: FunctionParams, aggregationFunctionName?: FunctionName): string {
    let functionLegend = functionParams
      ? (functionParams.functionName + (functionParams.timePeriod ? (' (' + functionParams.timePeriod.text + ')') : ''))
      : '';
    functionLegend = this.applyAggregationToFunctionLabel(functionLegend, aggregationFunctionName);
    functionLegend = functionLegend
      ? (functionLegend + ' - ')
      : '';
    const favouriteLegend = favouriteData.favourite.equipment.name + ', ' + favouriteData.favourite.metadata.name + ', '  + favouriteData.getUnitName();
    const zoneLegend = favouriteData.favourite.zone.system
      ? ''
      : (', ' + favouriteData.favourite.zone.level.level_name + ', ' + favouriteData.favourite.zone.zone_name.zone_name);
    return functionLegend + favouriteLegend + zoneLegend;
  }

  static buildEquipmentMetadataLabel(equipmentName: string, metadataName: string): string {
    return equipmentName + ', ' + metadataName;
  }

  static buildCountLabel(valueLabel: string): string {
    return this.COUNT_AGGREGATION_LABEL + ' - ' + valueLabel;
  }

  static applyAggregationToFunctionLabel(functionText: string, aggregationFunctionName: FunctionName | null): string {
    if (aggregationFunctionName == null) {
      return functionText;
    }
    return functionText
      ? (aggregationFunctionName + '(' + functionText + ')')
      : aggregationFunctionName;
  }

  static createPreviousSeriesId(seriesId: string): string {
    return seriesId + this.PREVIOUS_ID;
  }

  static createMainSeriesId(previousSeriesId: string): string {
    if (this.isPreviousSeriesId(previousSeriesId)) {
      return previousSeriesId.substring(0, previousSeriesId.length - this.PREVIOUS_ID.length);
    } else {
      throw new Error('Expected previous series id ' + previousSeriesId);
    }
  }

  static isPreviousSeriesId(seriesId: string): boolean {
    return !!(seriesId?.endsWith(this.PREVIOUS_ID));
  }

  static alignAllYAxis(chartSingleSeriesComponentsRef: QueryList<ChartComponent>): void {
    this.alignYAxis(chartSingleSeriesComponentsRef, 'axisY');
    this.alignYAxis(chartSingleSeriesComponentsRef, 'axisY2');
  }

  private static alignYAxis(chartSingleSeriesComponentsRef: QueryList<ChartComponent>, yAxisKey: 'axisY' | 'axisY2'): void {
    let maxLabelWidth = null;
    chartSingleSeriesComponentsRef?.forEach(targetChart => {
      const labelWidth = this.getTotalAxisWidth(targetChart?.chart?.[yAxisKey]);
      maxLabelWidth = maxLabelWidth == null ? labelWidth : Math.max(maxLabelWidth, labelWidth);
    });

    if (maxLabelWidth > 0) {
      chartSingleSeriesComponentsRef?.forEach(targetChart => {
        const yAxis = targetChart?.chart?.[yAxisKey];
        if (yAxis?.length) {
          const labelWidthOffset = maxLabelWidth - this.getTotalAxisWidth(yAxis);
          const axis = yAxis[yAxis.length - 1];
          axis.set('margin', labelWidthOffset > 0 ? labelWidthOffset : 0, true);
        }
      });
    }
  }

  /** Gets width of axis. On axis Y, `x2` and `x1` are the bounds of the label so `x2-x1` is the width of the label */
  private static getTotalAxisWidth(yAxis: ChartAxisY[]): number {
    return (yAxis || []).reduce((acc, axis) => {
      const x2: number = axis?.get('bounds')?.['x2'] ?? 0;
      const x1: number = axis?.get('bounds')?.['x1'] ?? 0;
      return acc + (x2 - x1);
    }, 0);
  }

  private static getChartContainerParentOffsetTop(parentElement: HTMLElement): number {
    return parentElement
      ? parentElement.offsetTop + (this.getChartContainerParentOffsetTop(<HTMLElement>parentElement.offsetParent))
      : 0;
  }

  private static getInteractiveAreaDistanceFromTop(chart: ChartComponent): number {
    return this.CHART_PADDING_PX + (chart.chart.title.text ? this.CHART_TITLE_HEIGHT : 0);
  }

  static shareMouseOverAcrossCharts(event: MouseEvent, currentChart: ChartComponent, chartSingleSeriesComponentsRef: QueryList<ChartComponent> | readonly ChartComponent[]): void {
    const chartAxisY = currentChart.chart.axisY[0];
    if (chartAxisY) {
      const sourceChartBoundsHeight = chartAxisY['bounds'].height;
      const sourceTs = currentChart.chart.axisX[0].convertPixelToValue(event.clientX);
      const parentOffsetTop = this.getChartContainerParentOffsetTop(currentChart.chartContainer.nativeElement.offsetParent);
      // the offsetTop is relative to its parent
      const distanceFromSourceChartTopOffset = event.pageY - parentOffsetTop - currentChart.chartContainer.nativeElement.offsetTop;
      const scrollDifference = event.pageY - event.clientY;
      const currentInteractiveAreaDistanceFromTop = this.getInteractiveAreaDistanceFromTop(currentChart);

      chartSingleSeriesComponentsRef?.forEach(targetChart => {
        if (targetChart !== currentChart) {
          const xAxis = targetChart.chart.axisX[0];
          if (xAxis) {
            const targetChartBoundsHeight = targetChart.chart.axisY[0]['bounds'].height;
            const scaleRatio = targetChartBoundsHeight / sourceChartBoundsHeight;
            const targetInteractiveAreaDistanceFromTop = this.getInteractiveAreaDistanceFromTop(targetChart);
            let distanceFromTargetChartTopOffset: number;
            if (distanceFromSourceChartTopOffset > currentInteractiveAreaDistanceFromTop) {
              // scale only the distance moved inside interactive area (exclude chart padding and chart title if any)
              distanceFromTargetChartTopOffset = (distanceFromSourceChartTopOffset - currentInteractiveAreaDistanceFromTop) * scaleRatio + targetInteractiveAreaDistanceFromTop + 1;
            } else {
              distanceFromTargetChartTopOffset = distanceFromSourceChartTopOffset;
            }
            const targetYPixel = parentOffsetTop
              + targetChart.chartContainer.nativeElement.offsetTop
              + distanceFromTargetChartTopOffset
              - scrollDifference;
            const targetXPixel = xAxis.convertValueToPixel(sourceTs);
            targetChart.chartContainer.nativeElement.childNodes[0].children[1].dispatchEvent(new MouseEvent(event.type, {
              view: window,
              bubbles: true,
              cancelable: true,
              screenX: event.screenX,
              clientX: targetXPixel,
              clientY: targetYPixel
            }));
          }
        }
      });
    }
  }

  static syncZoomAndPanAcrossCharts(event, currentChart: MiniChartComponent, chartSingleSeriesComponentsRef: QueryList<MiniChartComponent>): void {
    const asArray = (axis: AxisRange | AxisRange[]) => Array.isArray(axis) ? axis : (axis != null ? [axis]: []);

    chartSingleSeriesComponentsRef?.forEach(targetChart => {
      if (targetChart !== currentChart || event.trigger === 'reset') {
        // http://jsfiddle.net/canvasjs/mydmun2x/
        if (event.trigger === 'reset') {
          this.syncRangeForAxis(null, asArray(targetChart.chart.options.axisX));
          this.syncRangeForAxis(null, asArray(targetChart.chart.options.axisX2));
          this.syncRangeForAxis(null, asArray(targetChart.chart.options.axisY));
          this.syncRangeForAxis(null, asArray(targetChart.chart.options.axisY2));
        } else if (event.trigger === 'zoom' || event.trigger === 'pan') {
          this.syncRangeForAxis(event.axisX, asArray(targetChart.chart.options.axisX));
          this.syncRangeForAxis(event.axisX2, asArray(targetChart.chart.options.axisX2));
          this.syncRangeForAxis(event.axisY, asArray(targetChart.chart.options.axisY));
          this.syncRangeForAxis(event.axisY2, asArray(targetChart.chart.options.axisY2));
        }
        targetChart.chart.render();
      }
    });
  }

  private static syncRangeForAxis(source: AxisRange[], target: AxisRange[]): void {
    if (source != null) {
      source.forEach((s, index) => {
        if (index < target?.length) {
          const t = target[index];
          if (t != null) {
            t.viewportMinimum = s.viewportMinimum;
            t.viewportMaximum = s.viewportMaximum;
          }
        }
      });
    } else {
      target?.forEach(t => {
        if (t != null) {
          t.viewportMinimum = null;
          t.viewportMaximum = null;
        }
      });
    }
  }

  static addMenuOption(extendedToolbar, label: string, tooltip: string, where: InsertPosition, callback: () => void, customDataAttribute?: string): void {
    if (where === 'afterbegin' || where === 'beforeend') {
      const normalStyle = 'line-height: 12px; padding: 12px 8px; background-color: white; color: black;';
      const highlightedStyle = 'line-height: 12px; padding: 12px 8px; background-color: #2196F3; color: white';
      // if no custom data attribute supplied add the default one
      const html = '<div ' + (customDataAttribute ? customDataAttribute : ChartsUtil.CHART_MENU_DEFAULT_DATA_ATTRIBUTE)
        + (tooltip ? ' title="' + tooltip + '"' : '')
        + ' style="' + normalStyle + '">' + label + '</div>';
      extendedToolbar.insertAdjacentHTML(where, html);
      const buttonRef = extendedToolbar.childNodes[where === 'afterbegin' ? 0 : extendedToolbar.childNodes.length - 1];
      buttonRef.addEventListener('click', callback);
      buttonRef.addEventListener('mouseenter', function (event) {
        event.target.style = highlightedStyle;
      }, false);
      buttonRef.addEventListener('mouseleave', function (event) {
        event.target.style = normalStyle;
      }, false);
    } else {
      throw new Error('Only support position of "afterbegin" and "beforeend" not "' + where + '"');
    }
  }
}


export class FavouriteData {

  constructor(public readonly favourite: Favourite,
              private readonly transformData?: FavouriteTransformData | null) {
  }

  getUnitName(): string {
    return (this.transformData && this.transformData.unitFn) ? this.transformData.unitFn() : this.favourite.metadata.unit.unit;
  }

  transform(v: number): number {
    return this.transformData && this.transformData.transformFn ? this.transformData.transformFn(v) : v;
  }
}
